import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import {
  Folder,
  File,
  X,
  Save,
  Loader2,
  ChevronRight,
  ChevronDown,
  FileText,
} from 'lucide-react';
import { accessControlService, FileAccessGrant } from '../../services/accessControlService';
import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../../aws-config';

interface FileAccessEditorProps {
  userId: string;
  userEmail: string;
  onClose: () => void;
  onSaved?: () => void;
}

interface FileItem {
  key: string;
  name: string;
  size?: number;
  lastModified?: string;
}

interface FolderNode {
  name: string;
  path: string;
  files: FileItem[];
  subfolders: Map<string, FolderNode>;
  expanded: boolean;
}

export const FileAccessEditor: React.FC<FileAccessEditorProps> = ({
  userId,
  userEmail,
  onClose,
  onSaved,
}) => {
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingGrants, setIsLoadingGrants] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [grants, setGrants] = useState<FileAccessGrant[]>([]);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    setError(null);
    await Promise.all([loadFiles(), loadGrants()]);
  };

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const adminUserId = session.tokens?.idToken?.payload?.sub as string;

      if (!token || !adminUserId) {
        throw new Error('Not authenticated');
      }

      // Fetch admin's files from the file list API
      const response = await fetch(
        `${lambdaEndpoints.fileList}?user_id=${adminUserId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const data = await response.json();
      setFiles(data.files || []);
    } catch (err: any) {
      console.error('Error loading files:', err);
      setError(err.message || 'Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadGrants = async () => {
    setIsLoadingGrants(true);
    try {
      const result = await accessControlService.getFileAccess(userId);
      if (result.success && result.grants) {
        setGrants(result.grants);
        // Initialize selected paths from existing grants
        const paths = new Set(result.grants.map(g => g.path));
        setSelectedPaths(paths);
      }
    } catch (err: any) {
      console.error('Error loading grants:', err);
    } finally {
      setIsLoadingGrants(false);
    }
  };

  // Build folder tree from flat file list
  const folderTree = useMemo(() => {
    const root: FolderNode = {
      name: 'Files',
      path: '',
      files: [],
      subfolders: new Map(),
      expanded: true,
    };

    files.forEach(file => {
      // Remove the users/{id}/data/ prefix for display
      const pathParts = file.key.split('/');
      // Skip users/{id}/data prefix if present
      let displayParts = pathParts;
      if (pathParts[0] === 'users' && pathParts.length > 3 && pathParts[2] === 'data') {
        displayParts = pathParts.slice(3);
      }

      if (displayParts.length === 1) {
        // Root level file
        root.files.push({
          ...file,
          name: displayParts[0],
        });
      } else {
        // File in subfolder
        let currentNode = root;
        const folders = displayParts.slice(0, -1);
        let currentPath = '';

        // Get the base path (users/{id}/data/)
        const basePath = pathParts.slice(0, 3).join('/') + '/';

        folders.forEach((folderName, idx) => {
          currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;
          const fullPath = basePath + currentPath + '/';

          if (!currentNode.subfolders.has(folderName)) {
            currentNode.subfolders.set(folderName, {
              name: folderName,
              path: fullPath,
              files: [],
              subfolders: new Map(),
              expanded: expandedFolders.has(fullPath),
            });
          }
          currentNode = currentNode.subfolders.get(folderName)!;
        });

        currentNode.files.push({
          ...file,
          name: displayParts[displayParts.length - 1],
        });
      }
    });

    return root;
  }, [files, expandedFolders]);

  const toggleFolder = (path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const toggleSelection = (path: string, isFolder: boolean) => {
    setSelectedPaths(prev => {
      const next = new Set(prev);
      const fullPath = isFolder ? path : path;

      if (next.has(fullPath)) {
        next.delete(fullPath);
      } else {
        next.add(fullPath);
      }
      return next;
    });
  };

  const isPathSelected = (path: string): boolean => {
    // Check exact match
    if (selectedPaths.has(path)) return true;

    // Check if parent folder is selected (for files)
    const paths = Array.from(selectedPaths);
    for (const selectedPath of paths) {
      if (selectedPath.endsWith('/') && path.startsWith(selectedPath)) {
        return true;
      }
    }
    return false;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Convert selected paths to grants
      const newGrants: FileAccessGrant[] = Array.from(selectedPaths).map(path => ({
        path,
        type: path.endsWith('/') ? 'folder' : 'file',
      }));

      const result = await accessControlService.setFileAccess(userId, newGrants);

      if (result.success) {
        toast.success(`File access updated for ${userEmail}`);
        onSaved?.();
        onClose();
      } else {
        toast.error(result.error || 'Failed to save file access');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to save file access');
    } finally {
      setIsSaving(false);
    }
  };

  const renderFolder = (node: FolderNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.path) || depth === 0;
    const isSelected = selectedPaths.has(node.path);
    const hasContent = node.files.length > 0 || node.subfolders.size > 0;

    return (
      <div key={node.path || 'root'} className="select-none">
        {depth > 0 && (
          <div
            className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-muted/50 ${
              isSelected ? 'bg-primary/10' : ''
            }`}
            style={{ paddingLeft: `${depth * 20 + 8}px` }}
          >
            <button
              onClick={() => toggleFolder(node.path)}
              className="p-0.5 hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleSelection(node.path, true)}
            />
            <Folder className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium">{node.name}</span>
            {isSelected && (
              <Badge variant="secondary" className="text-xs ml-auto">
                Folder Access
              </Badge>
            )}
          </div>
        )}

        {(isExpanded || depth === 0) && (
          <div>
            {/* Subfolders */}
            {Array.from(node.subfolders.values()).map(subfolder =>
              renderFolder(subfolder, depth + 1)
            )}

            {/* Files */}
            {node.files.map(file => {
              const fileSelected = isPathSelected(file.key);
              const parentSelected = Array.from(selectedPaths).some(
                p => p.endsWith('/') && file.key.startsWith(p)
              );

              return (
                <div
                  key={file.key}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer hover:bg-muted/50 ${
                    fileSelected ? 'bg-primary/10' : ''
                  }`}
                  style={{ paddingLeft: `${(depth + 1) * 20 + 28}px` }}
                  onClick={() => !parentSelected && toggleSelection(file.key, false)}
                >
                  <Checkbox
                    checked={fileSelected}
                    disabled={parentSelected}
                    onCheckedChange={() => toggleSelection(file.key, false)}
                  />
                  <FileText className="h-4 w-4 text-blue-500" />
                  <span className="text-sm truncate flex-1">{file.name}</span>
                  {parentSelected && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      via folder
                    </Badge>
                  )}
                  {fileSelected && !parentSelected && (
                    <Badge variant="secondary" className="text-xs">
                      File Access
                    </Badge>
                  )}
                </div>
              );
            })}

            {depth === 0 && !hasContent && (
              <div className="text-sm text-muted-foreground text-center py-8">
                No files available to share
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const isLoading = isLoadingFiles || isLoadingGrants;
  const selectedCount = selectedPaths.size;
  const hasChanges = useMemo(() => {
    const originalPaths = new Set(grants.map(g => g.path));
    if (originalPaths.size !== selectedPaths.size) return true;
    const currentPaths = Array.from(selectedPaths);
    for (const path of currentPaths) {
      if (!originalPaths.has(path)) return true;
    }
    return false;
  }, [grants, selectedPaths]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <File className="h-5 w-5" />
                File Access for {userEmail}
              </CardTitle>
              <CardDescription className="mt-1">
                Select files and folders this user can access. Non-admin users can only see files you grant them access to.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto py-4">
          {error && (
            <div className="text-sm text-destructive mb-4 p-3 bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-[90%]" />
              <Skeleton className="h-8 w-[95%]" />
              <Skeleton className="h-8 w-[85%]" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="p-2 bg-muted/50 border-b">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Your Files</span>
                  <Badge variant="outline">
                    {selectedCount} selected
                  </Badge>
                </div>
              </div>
              <div className="max-h-[400px] overflow-auto p-2">
                {renderFolder(folderTree)}
              </div>
            </div>
          )}

          {selectedCount > 0 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <div className="text-sm font-medium mb-2">Access Summary:</div>
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedPaths).map(path => (
                  <Badge
                    key={path}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {path.endsWith('/') ? (
                      <Folder className="h-3 w-3" />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                    <span className="truncate max-w-[200px]">
                      {path.split('/').filter(Boolean).pop()}
                    </span>
                    <button
                      onClick={() => toggleSelection(path, path.endsWith('/'))}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>

        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {hasChanges ? (
              <span className="text-amber-500">Unsaved changes</span>
            ) : (
              <span>No changes</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Access
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
